import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RecruitmentProvider, useRecruitment } from '../context/RecruitmentContext';
import { useSessionLaunch } from '../lib/useSessionLaunch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LayoutDashboard, Users, Briefcase, GitBranch, Calendar, ClipboardList, Settings,
  LogOut, ChevronDown, Plus, Upload, Search, Filter, BarChart3
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';

// Import sub-components
import RecruitmentDashboard from '../components/recruitment/RecruitmentDashboard';
import CandidateList from '../components/recruitment/CandidateList';
import CandidateDetail from '../components/recruitment/CandidateDetail';
import JobList from '../components/recruitment/JobList';
import JobDetail from '../components/recruitment/JobDetail';
import KanbanBoard from '../components/recruitment/KanbanBoard';
import InterviewList from '../components/recruitment/InterviewList';
import RecruitmentSettings from '../components/recruitment/RecruitmentSettings';
import AuditLogs from '../components/recruitment/AuditLogs';
import RecruitmentReports from '../components/recruitment/RecruitmentReports';
import { ALL, isDay } from '../components/recruitment/reportUtils';

// Reports tab state in the URL: ?report=funnel, or ?report=candidates with
// &report_job=<id>&report_stage=<current stage>&report_reached=<stage passed through>.
// Every report also takes &report_from=&report_to= (yyyy-MM-dd days) and
// &report_basis=moved|added (what the dates select, where a report has a switch).
// The current-stage summary is the default, so it needs no params.
const REPORT_PARAMS = ['report', 'report_job', 'report_stage', 'report_reached', 'report_from', 'report_to', 'report_basis'];
const REPORT_VIEWS = ['summary', 'funnel', 'candidates'];
const REPORT_BASES = ['moved', 'added'];

const normalizeReportFilters = ({ view, job, stage, reached, from, to, basis } = {}) => ({
  view: REPORT_VIEWS.includes(view) ? view : 'summary',
  job: job || ALL,
  stage: stage || ALL,
  reached: reached || ALL,
  from: isDay(from) ? from : ALL,
  to: isDay(to) ? to : ALL,
  // ALL = the report's own default (moved for Passed through, added for the candidate report)
  basis: REPORT_BASES.includes(basis) ? basis : ALL
});

// Writes `filters` onto `params`; null clears them (any tab but Reports).
const setReportParams = (params, filters) => {
  REPORT_PARAMS.forEach((key) => params.delete(key));
  if (!filters) return params;
  if (filters.view !== 'summary') params.set('report', filters.view);
  if (filters.view === 'candidates') {
    if (filters.job !== ALL) params.set('report_job', filters.job);
    if (filters.stage !== ALL) params.set('report_stage', filters.stage);
    if (filters.reached !== ALL) params.set('report_reached', filters.reached);
  }
  if (filters.from !== ALL) params.set('report_from', filters.from);
  if (filters.to !== ALL) params.set('report_to', filters.to);
  if (filters.view !== 'summary' && filters.basis !== ALL) params.set('report_basis', filters.basis);
  return params;
};

// Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Card className="w-[400px]">
            <CardHeader>
              <CardTitle className="text-red-600">Something went wrong</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">{this.state.error?.message}</p>
              <Button onClick={() => window.location.reload()}>Refresh Page</Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

const RecruitmentContent = ({ username, role }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isReady, fetchPipelineTemplates, fetchJobs, getDashboardStats } = useRecruitment();
  
  // Set page title and favicon
  usePageMeta(PAGE_META.RECRUITMENT_PORTAL.title, PAGE_META.RECRUITMENT_PORTAL.favicon);
  
  // Get active tab from URL params
  const tabFromUrl = searchParams.get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [stats, setStats] = useState(null);
  const [candidateSearchFromDashboard, setCandidateSearchFromDashboard] = useState('');
  
  // Detail view states
  const [selectedCandidateId, setSelectedCandidateId] = useState(searchParams.get('candidate_id') || null);
  const [selectedJobId, setSelectedJobId] = useState(searchParams.get('job_id') || null);
  const [selectedInterviewId, setSelectedInterviewId] = useState(searchParams.get('interview_id') || null);
  // The open job's own tab (overview / pipeline / candidates / interviews).
  const [jobTab, setJobTab] = useState(searchParams.get('job_tab') || 'overview');
  
  // Report view + filters. Owned here and passed down rather than read from
  // the URL inside the report: react-router applies setSearchParams in a
  // transition, so a report mounted by the same click would see the old URL.
  // Kept when leaving the tab, so coming back shows the same report.
  const [reportFilters, setReportFilters] = useState(() => normalizeReportFilters({
    view: searchParams.get('report'),
    job: searchParams.get('report_job'),
    stage: searchParams.get('report_stage'),
    reached: searchParams.get('report_reached'),
    from: searchParams.get('report_from'),
    to: searchParams.get('report_to'),
    basis: searchParams.get('report_basis')
  }));

  // Build URL for a given tab (for right-click "Open in new tab")
  const getTabUrl = (tabName) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tabName);
    params.delete('candidate_id');
    params.delete('job_id');
    params.delete('job_tab');
    params.delete('interview_id');
    setReportParams(params, tabName === 'reports' ? reportFilters : null);
    return `${window.location.pathname}?${params.toString()}`;
  };

  // Build deep-link URLs for individual entities (right-click "Open in new tab" support)
  const buildEntityUrl = (params) => `${window.location.pathname}?${params.toString()}`;

  const getCandidateUrl = (candidateId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'candidates');
    params.set('candidate_id', candidateId);
    params.delete('job_id');
    params.delete('job_tab');
    params.delete('interview_id');
    setReportParams(params, null);
    return buildEntityUrl(params);
  };

  const getJobUrl = (jobId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'jobs');
    params.set('job_id', jobId);
    params.delete('candidate_id');
    params.delete('interview_id');
    setReportParams(params, null);
    return buildEntityUrl(params);
  };

  const getInterviewUrl = (interviewId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'interviews');
    params.set('interview_id', interviewId);
    params.delete('candidate_id');
    params.delete('job_id');
    params.delete('job_tab');
    setReportParams(params, null);
    return buildEntityUrl(params);
  };

  const buildReportParams = (filters) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'reports');
    params.delete('candidate_id');
    params.delete('job_id');
    params.delete('job_tab');
    params.delete('interview_id');
    params.delete('search');
    return setReportParams(params, filters);
  };

  const getReportUrl = (filters) => buildEntityUrl(buildReportParams(normalizeReportFilters(filters)));

  // Switch report view and/or the candidate report's job, stage and
  // passed-through filters.
  const handleReportNavigate = (filters) => {
    const next = normalizeReportFilters(filters);
    setActiveTab('reports');
    setReportFilters(next);
    setSearchParams(buildReportParams(next), { replace: true });
  };

  // Update URL when tab changes
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSelectedCandidateId(null);
    setSelectedJobId(null);
    setSelectedInterviewId(null);
    if (newTab !== 'candidates') {
      setCandidateSearchFromDashboard('');
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    newParams.delete('candidate_id');
    newParams.delete('job_id');
    newParams.delete('job_tab');
    newParams.delete('interview_id');
    newParams.delete('search');
    setReportParams(newParams, newTab === 'reports' ? reportFilters : null);
    setSearchParams(newParams, { replace: true });
  };

  // Navigate to candidate detail page
  const handleViewCandidate = (candidateId) => {
    setActiveTab('candidates');
    setSelectedCandidateId(candidateId);
    setSelectedJobId(null); // Clear job selection when viewing candidate
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'candidates');
    newParams.set('candidate_id', candidateId);
    newParams.delete('job_id');
    newParams.delete('job_tab');
    setReportParams(newParams, null);
    setSearchParams(newParams, { replace: true });
  };

  // A candidate opened from a job stays inside that job (?tab=jobs&job_id=
  // &job_tab=&candidate_id=), so going back reopens the job on the same tab.
  const handleViewCandidateFromJob = (candidateId) => {
    setSelectedCandidateId(candidateId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('candidate_id', candidateId);
    setSearchParams(newParams, { replace: true });
  };

  // Navigate back from candidate detail (to the list, or to the job it was opened from)
  const handleBackFromCandidate = () => {
    setSelectedCandidateId(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('candidate_id');
    setSearchParams(newParams, { replace: true });
  };

  // Navigate to job detail page
  const handleViewJob = (jobId) => {
    setSelectedJobId(jobId);
    setJobTab('overview');
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'jobs');
    newParams.set('job_id', jobId);
    newParams.delete('job_tab');
    setSearchParams(newParams, { replace: true });
  };

  const handleJobTabChange = (tab) => {
    setJobTab(tab);
    const newParams = new URLSearchParams(searchParams);
    if (tab === 'overview') newParams.delete('job_tab');
    else newParams.set('job_tab', tab);
    setSearchParams(newParams, { replace: true });
  };

  // Navigate back from job detail
  const handleBackFromJob = () => {
    setSelectedJobId(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('job_id');
    newParams.delete('job_tab');
    setSearchParams(newParams, { replace: true });
  };

  // Handle search from Dashboard — navigate to candidates tab with the search applied
  const handleSearchCandidates = (query) => {
    setCandidateSearchFromDashboard(query);
    setActiveTab('candidates');
    setSelectedCandidateId(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'candidates');
    newParams.set('search', query);
    newParams.delete('candidate_id');
    newParams.delete('job_id');
    newParams.delete('job_tab');
    setReportParams(newParams, null);
    setSearchParams(newParams, { replace: true });
  };

  // Load initial data
  useEffect(() => {
    if (isReady) {
      fetchPipelineTemplates();
      fetchJobs();
      loadStats();
    }
  }, [isReady]);

  const loadStats = async () => {
    const data = await getDashboardStats();
    setStats(data);
  };

  const handleLogout = () => {
    window.close();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Harmony Recruitment</h1>
                <p className="text-xs text-slate-500">AI-Powered Hiring</p>
              </div>
            </div>
            
            {/* Tabs. The full bar needs ~1200px, so narrower screens get the
                scrollable strip below the header instead. */}
            <div className="hidden xl:flex">
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="bg-transparent gap-1">
                  <TabsTrigger 
                    value="dashboard" 
                    className="gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                    data-testid="tab-dashboard"
                    asChild
                  >
                    <a href={getTabUrl('dashboard')} onClick={(e) => { e.preventDefault(); handleTabChange('dashboard'); }}>
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </a>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="candidates" 
                    className="gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                    data-testid="tab-candidates"
                    asChild
                  >
                    <a href={getTabUrl('candidates')} onClick={(e) => { e.preventDefault(); handleTabChange('candidates'); }}>
                      <Users className="w-4 h-4" />
                      Candidates
                    </a>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="kanban" 
                    className="gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                    data-testid="tab-kanban"
                    asChild
                  >
                    <a href={getTabUrl('kanban')} onClick={(e) => { e.preventDefault(); handleTabChange('kanban'); }}>
                      <GitBranch className="w-4 h-4" />
                      Pipeline
                    </a>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="jobs" 
                    className="gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                    data-testid="tab-jobs"
                    asChild
                  >
                    <a href={getTabUrl('jobs')} onClick={(e) => { e.preventDefault(); handleTabChange('jobs'); }}>
                      <Briefcase className="w-4 h-4" />
                      Jobs
                    </a>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="interviews" 
                    className="gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                    data-testid="tab-interviews"
                    asChild
                  >
                    <a href={getTabUrl('interviews')} onClick={(e) => { e.preventDefault(); handleTabChange('interviews'); }}>
                      <Calendar className="w-4 h-4" />
                      Interviews
                    </a>
                  </TabsTrigger>
                  <TabsTrigger
                    value="reports"
                    className="gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                    data-testid="tab-reports"
                    asChild
                  >
                    <a href={getTabUrl('reports')} onClick={(e) => { e.preventDefault(); handleTabChange('reports'); }}>
                      <BarChart3 className="w-4 h-4" />
                      Reports
                    </a>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="settings" 
                    className="gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                    data-testid="tab-settings"
                    asChild
                  >
                    <a href={getTabUrl('settings')} onClick={(e) => { e.preventDefault(); handleTabChange('settings'); }}>
                      <Settings className="w-4 h-4" />
                      Settings
                    </a>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="audit" 
                    className="gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                    data-testid="tab-audit"
                    asChild
                  >
                    <a href={getTabUrl('audit')} onClick={(e) => { e.preventDefault(); handleTabChange('audit'); }}>
                      <ClipboardList className="w-4 h-4" />
                      Audit
                    </a>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-purple-700">
                      {username?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  {/* Hidden where the full tab bar leaves no room for it. */}
                  <span className="hidden sm:inline xl:hidden 2xl:inline text-sm">{username}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{username}</p>
                  <p className="text-xs text-muted-foreground capitalize">{role?.replace('_', ' ')}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Close App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Mobile Tabs */}
        <div className="xl:hidden pb-4 -mx-4 px-4 overflow-x-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="bg-transparent gap-1 w-max">
              <TabsTrigger value="dashboard" className="text-xs px-3" asChild>
                <a href={getTabUrl('dashboard')} onClick={(e) => { e.preventDefault(); handleTabChange('dashboard'); }}>
                  <LayoutDashboard className="w-3 h-3 mr-1" />
                  Dashboard
                </a>
              </TabsTrigger>
              <TabsTrigger value="candidates" className="text-xs px-3" asChild>
                <a href={getTabUrl('candidates')} onClick={(e) => { e.preventDefault(); handleTabChange('candidates'); }}>
                  <Users className="w-3 h-3 mr-1" />
                  Candidates
                </a>
              </TabsTrigger>
              <TabsTrigger value="kanban" className="text-xs px-3" asChild>
                <a href={getTabUrl('kanban')} onClick={(e) => { e.preventDefault(); handleTabChange('kanban'); }}>
                  <GitBranch className="w-3 h-3 mr-1" />
                  Pipeline
                </a>
              </TabsTrigger>
              <TabsTrigger value="jobs" className="text-xs px-3" asChild>
                <a href={getTabUrl('jobs')} onClick={(e) => { e.preventDefault(); handleTabChange('jobs'); }}>
                  <Briefcase className="w-3 h-3 mr-1" />
                  Jobs
                </a>
              </TabsTrigger>
              <TabsTrigger value="interviews" className="text-xs px-3" asChild>
                <a href={getTabUrl('interviews')} onClick={(e) => { e.preventDefault(); handleTabChange('interviews'); }}>
                  <Calendar className="w-3 h-3 mr-1" />
                  Interviews
                </a>
              </TabsTrigger>
              <TabsTrigger value="reports" className="text-xs px-3" asChild>
                <a href={getTabUrl('reports')} onClick={(e) => { e.preventDefault(); handleTabChange('reports'); }}>
                  <BarChart3 className="w-3 h-3 mr-1" />
                  Reports
                </a>
              </TabsTrigger>
              <TabsTrigger value="settings" className="text-xs px-3" asChild>
                <a href={getTabUrl('settings')} onClick={(e) => { e.preventDefault(); handleTabChange('settings'); }}>
                  <Settings className="w-3 h-3 mr-1" />
                  Settings
                </a>
              </TabsTrigger>
              <TabsTrigger value="audit" className="text-xs px-3" asChild>
                <a href={getTabUrl('audit')} onClick={(e) => { e.preventDefault(); handleTabChange('audit'); }}>
                  <ClipboardList className="w-3 h-3 mr-1" />
                  Audit
                </a>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'dashboard' && <RecruitmentDashboard onRefresh={loadStats} onSearchCandidates={handleSearchCandidates} onViewCandidate={handleViewCandidate} />}
          {activeTab === 'candidates' && (
            selectedCandidateId ? (
              <CandidateDetail 
                candidateId={selectedCandidateId} 
                onBack={handleBackFromCandidate}
                getJobUrl={getJobUrl}
              />
            ) : (
              <CandidateList onViewCandidate={handleViewCandidate} initialSearch={candidateSearchFromDashboard} getCandidateUrl={getCandidateUrl} />
            )
          )}
          {activeTab === 'kanban' && <KanbanBoard onViewCandidate={handleViewCandidate} getCandidateUrl={getCandidateUrl} />}
          {activeTab === 'jobs' && (
            selectedJobId && selectedCandidateId ? (
              <CandidateDetail
                candidateId={selectedCandidateId}
                onBack={handleBackFromCandidate}
                getJobUrl={getJobUrl}
              />
            ) : selectedJobId ? (
              <JobDetail 
                jobId={selectedJobId} 
                onBack={handleBackFromJob}
                onViewCandidate={handleViewCandidateFromJob}
                getCandidateUrl={getCandidateUrl}
                initialTab={jobTab}
                onTabChange={handleJobTabChange}
              />
            ) : (
              <JobList onViewJob={handleViewJob} getJobUrl={getJobUrl} />
            )
          )}
          {activeTab === 'interviews' && (
            <InterviewList
              onViewCandidate={handleViewCandidate}
              getInterviewUrl={getInterviewUrl}
              getCandidateUrl={getCandidateUrl}
              initialEditInterviewId={selectedInterviewId}
              onEditDialogClose={() => {
                setSelectedInterviewId(null);
                const np = new URLSearchParams(searchParams);
                np.delete('interview_id');
                setSearchParams(np, { replace: true });
              }}
            />
          )}
          {activeTab === 'reports' && (
            <RecruitmentReports
              filters={reportFilters}
              onNavigate={handleReportNavigate}
              getReportUrl={getReportUrl}
              getCandidateUrl={getCandidateUrl}
            />
          )}
          {activeTab === 'settings' && <RecruitmentSettings />}
          {activeTab === 'audit' && <AuditLogs />}
        </div>
      </main>
    </div>
  );
};

const RecruitmentPortalApp = () => {
  // Signed in as whoever opens the page, from their own session, so shared
  // links work (see useSessionLaunch).
  const { token, username, role } = useSessionLaunch();

  return (
    <ErrorBoundary>
      <RecruitmentProvider token={token}>
        <RecruitmentContent username={username} role={role} />
      </RecruitmentProvider>
    </ErrorBoundary>
  );
};

export default RecruitmentPortalApp;
